import type { Building } from "building-types/building/buildingTypes";
import type { ComponentTransform } from "building-types/components/componentTypes";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type { OptionChoices } from "building-types/options/optionChoices";
import type { OptionDefinitions } from "building-types/options/optionDefinitions";
import type { RelatedStairSystemsMap } from "building-types/stairs/relatedStairSystemsMapTypes";
import type { StairSystem } from "building-types/stairs/stairTypes";
import type { RWTable, Table } from "common/data/table";
import { evaluateEntityForComponentInstance, resolveOptionsInFormula } from "doc/formula/evalFormulas";
import { shouldGenerateEntity } from "../utils/entityIsolation";
import makeGenerationStageComparator from "../utils/makeGenerationStageComparator";
import memo from "../utils/memo";

/**
 * Loop through all the component instances and add the stair system instance to the map if it should be generated.
 */
export function addRelatedStairSystemPlacements(
  id: string,
  stairSystem: StairSystem,
  idToParents: Table<readonly string[]>,
  componentToTransforms: Table<readonly ComponentTransform[]>,
  isolatedEntity: DocEntityHandle | undefined,
  definitions: OptionDefinitions,
  optionChoices: OptionChoices,
  relatedStairSystemsMap: RWTable<ComponentTransform[]>,
): void {
  const parentComponents = idToParents[id] || [];
  for (const componentId of parentComponents) {
    const transforms = componentToTransforms[componentId] || [];
    for (const transform of transforms) {
      if (!shouldGenerateEntity(transform.instanceId, idToParents, isolatedEntity)) {
        continue;
      }

      const { componentInstanceChain } = transform;

      const evaluatedStairSystem = evaluateEntityForComponentInstance(
        stairSystem,
        componentInstanceChain,
        definitions,
        optionChoices,
      );
      if (evaluatedStairSystem.disabled) {
        continue;
      }

      relatedStairSystemsMap[id] ??= [];
      relatedStairSystemsMap[id].push(transform);
    }
  }
}

function select({
  doc: { stairSystems, optionSets, instanceOptionSets },
  optionChoices,
  enabledFeatures,
  idToParents,
  componentToTransforms,
  isolatedEntity,
}: Building) {
  const definitions = { optionSets, instanceOptionSets };
  return {
    stairSystems,
    enabledFeatures,
    idToParents: idToParents ?? {},
    componentToTransforms: componentToTransforms ?? {},
    isolatedEntity,
    definitions,
    optionChoices: resolveOptionsInFormula({ stairSystems }, definitions, optionChoices),
  };
}

const compare = makeGenerationStageComparator<typeof select>({
  optionChoices: "shallow",
  definitions: "shallow",
  idToParents: "shallow",
  componentToTransforms: "shallow",
  isolatedEntity: "shallow",
  enabledFeatures: "shallow",
  stairSystems: "shallow",
});

function generate({
  stairSystems,
  enabledFeatures,
  idToParents,
  componentToTransforms,
  isolatedEntity,
  definitions,
  optionChoices,
}: ReturnType<typeof select>): { relatedStairSystemsMap?: RelatedStairSystemsMap } {
  if (!enabledFeatures.modelComponents) {
    return {};
  }

  const relatedStairSystemsMap: RWTable<ComponentTransform[]> = {};

  for (const stairSystemId in stairSystems) {
    addRelatedStairSystemPlacements(
      stairSystemId,
      stairSystems[stairSystemId],
      idToParents,
      componentToTransforms,
      isolatedEntity,
      definitions,
      optionChoices,
      relatedStairSystemsMap,
    );
  }

  return { relatedStairSystemsMap };
}

export default memo(select, generate, compare);
